import React, { useCallback, useContext, useState } from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';

import { InventoryLocation } from './inventory-location';
import { LocationsHeader } from './locations-header';
import ModalPortal from '../../../../../../components/modal-portal';
import { useModal } from '../../../../../../hooks/use-modal';
import { usePusherEvent } from '../../../../../../hooks/use-pusher-event';
import { INVENTORY_ACTION_COMPLETE } from '../../../../../../services/pusher-service/pusher-contract-messages';
import Global from '../../../../../../store/global-selectors';
import { ProductDetailsContext } from '../../../../context';
import generalStyle from '../../../../style.module.less';
import { messages } from '../messages';
import { AddStockModal } from '../modals/add-alternate-location';
import { MoveInventoryModal } from '../modals/move-inventory';
import style from '../style.module.less';

const NoLocationsMade = () => {
  return (
    <div className={style['no-locations']}>
      <FormattedMessage {...messages.noLocations} />
    </div>
  );
};

export const LocationsGrid = () => {
  const {
    inventoryWarehouses,
    inventoryWarehouseLocations,
    isActive,
    productId,
    productName,
    productSku,
    inventoryLocations,
    isLoadingInventoryLocations,
    setShouldLoadInventoryLocations,
    productWarehouseLocation,
  } = useContext(ProductDetailsContext);
  const onReloadLocations = useCallback(() => {
    setShouldLoadInventoryLocations(true);
  }, [setShouldLoadInventoryLocations]);
  const currentUserId = useSelector(Global.selectUserCurrentUserId);
  const onReloadLocationsAfterPusherMessage = useCallback(
    (pusherMessage) => {
      if (pusherMessage && pusherMessage.UserID === currentUserId && pusherMessage.Success) {
        setShouldLoadInventoryLocations(true);
      }
    },
    [setShouldLoadInventoryLocations, currentUserId],
  );
  usePusherEvent(INVENTORY_ACTION_COMPLETE, onReloadLocationsAfterPusherMessage);

  const { open: openAddInventoryModal, close: closeAddInventoryModal, isOpen: isAddInventoryModalOpen } = useModal();
  const { open: openMoveInventoryModal, close: closeMoveInventoryModal, isOpen: isMoveInventoryModalOpen } = useModal();
  const [moveInventoryParams, setMoveInventoryParams] = useState(null);

  const onLaunchMoveInventoryModal = useCallback(
    (inventoryLocationId, lotUuid) => {
      setMoveInventoryParams({ inventoryLocationId, lotUuid });
      openMoveInventoryModal();
    },
    [openMoveInventoryModal],
  );

  const shouldDisableButton = !productSku || productId === 0 || !isActive;

  return (
    <>
      <div className={style['locations-container-flex']}>
        <div className={classnames(generalStyle['shipping-defaults-header-text'], style['allocations-title'])}>
          <FormattedMessage {...messages.warehousesHeader} />
        </div>
        <Button
          disabled={shouldDisableButton}
          disabledTooltipMessage={<FormattedMessage {...messages.locationsFooterLinkInactiveProduct} />}
          disabledTooltipMessagePosition='bottom'
          onClick={openAddInventoryModal}
          theme='secondary'
        >
          <FormattedMessage {...messages.locationsFooterLink} />
        </Button>
      </div>
      <div className={style['locations-container']}>
        <LocationsHeader />
        {isLoadingInventoryLocations ? <LoadingIndicator className={style['loading-indicator']} theme='grey' /> : null}
        {!isLoadingInventoryLocations &&
          inventoryLocations.map((inv, index) => {
            return (
              <InventoryLocation
                key={index}
                allocated={inv.allocated}
                available={inv.available}
                expirationDate={inv.expirationDate ?? ''}
                inventoryLocationId={inv.inventoryLocationId}
                location={inv.location}
                lot={inv.lot}
                lotUuid={inv.lotUuid}
                onReloadLocations={onReloadLocations}
                openMoveInventoryModal={onLaunchMoveInventoryModal}
                productActive={isActive}
                productId={productId}
                stock={inv.stock}
                warehouseName={inv.warehouseName}
              />
            );
          })}
        {!isLoadingInventoryLocations && inventoryLocations.length === 0 && <NoLocationsMade />}
      </div>
      {isAddInventoryModalOpen ? (
        <ModalPortal onClose={closeAddInventoryModal}>
          <AddStockModal
            inventoryWarehouseLocations={inventoryWarehouseLocations}
            inventoryWarehouses={inventoryWarehouses}
            onClose={closeAddInventoryModal}
            onReloadLocations={onReloadLocations}
            productId={productId}
            productName={productName}
            productSku={productSku}
            productWarehouseLocation={productWarehouseLocation}
          />
        </ModalPortal>
      ) : null}
      {isMoveInventoryModalOpen && moveInventoryParams ? (
        <ModalPortal onClose={closeMoveInventoryModal}>
          <MoveInventoryModal
            inventoryLocationId={moveInventoryParams.inventoryLocationId}
            inventoryWarehouseLocations={inventoryWarehouseLocations}
            inventoryWarehouses={inventoryWarehouses}
            lockFromLocation={inventoryLocations.length <= 1}
            lotUuid={moveInventoryParams.lotUuid}
            onClose={closeMoveInventoryModal}
            onReloadLocations={onReloadLocations}
            productId={productId}
            productName={productName}
            productSku={productSku}
          />
        </ModalPortal>
      ) : null}
    </>
  );
};
